import React, { useState } from 'react';
import { toast } from 'sonner';
import { Vote, Plus, Lock, Trash2, Check, X, MinusCircle, Loader2, EyeOff, Mail, BellRing } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Link } from 'react-router-dom';

export const ApplicationBadge = ({ motion, canManage }) => motion.application_id ? (
  canManage
    ? <Link to={`/admin/applications?open=${motion.application_id}`} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-title hover:underline" style={{ background: '#975a16', color: 'white' }} data-testid={`motion-application-badge-${motion.id}`} title="Open the membership application">Membership application</Link>
    : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-title" style={{ background: '#975a16', color: 'white' }} data-testid={`motion-application-badge-${motion.id}`}>Membership application</span>
) : null;

const CHOICES = [
  { v: 'yes', label: 'Yes', icon: Check, color: '#1c6b2c' },
  { v: 'no', label: 'No', icon: X, color: '#8a1f11' },
  { v: 'abstain', label: 'Abstain', icon: MinusCircle, color: '#9a6b00' },
];

function TallyBar({ tally }) {
  const total = (tally.yes + tally.no + tally.abstain) || 1;
  const seg = [
    { k: 'yes', c: '#1c6b2c' }, { k: 'no', c: '#8a1f11' }, { k: 'abstain', c: '#9a6b00' },
  ];
  return (
    <div className="mt-2">
      <div className="flex h-3 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.06)' }}>
        {seg.map((s) => tally[s.k] > 0 && <div key={s.k} style={{ width: `${(tally[s.k] / total) * 100}%`, background: s.c }} />)}
      </div>
      <div className="flex gap-4 text-xs mt-1.5" style={{ color: 'var(--olive)' }}>
        <span>Yes {tally.yes}</span><span>No {tally.no}</span><span>Abstain {tally.abstain}</span>
      </div>
    </div>
  );
}

function Motion({ meetingId, motion, canManage, onUpdate }) {
  const { castVote, adminCloseMotion, adminDeleteMotion, sendEmailBallots, remindNonVoters } = useAuth();
  const [busy, setBusy] = useState(false);
  const closed = motion.status === 'closed';

  const vote = async (choice) => {
    setBusy(true);
    try { onUpdate(await castVote(meetingId, motion.id, choice)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not record your vote.'); }
    finally { setBusy(false); }
  };
  const close = async () => { setBusy(true); try { onUpdate(await adminCloseMotion(meetingId, motion.id)); toast.success('Voting closed.'); } finally { setBusy(false); } };
  const del = async () => { if (!window.confirm('Delete this motion and all its votes?')) return; setBusy(true); try { onUpdate(await adminDeleteMotion(meetingId, motion.id)); } finally { setBusy(false); } };
  const emailBallots = async () => {
    setBusy(true);
    try {
      const r = await sendEmailBallots(meetingId, motion.id);
      if (r.mocked) toast.success(`Ballots prepared for ${r.recipients} attendee(s) (email is in preview/mock mode).`);
      else if (r.failed) toast.error(`Delivery failed for ${r.failed} of ${r.recipients} attendee(s). ${r.error ? '(' + String(r.error).slice(0, 120) + ')' : 'Check email settings.'}`);
      else toast.success(`Ballots emailed to ${r.sent} of ${r.recipients} attendee(s). Members can reply Yes/No/Abstain.`);
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send ballots.'); }
    finally { setBusy(false); }
  };
  const remind = async () => {
    setBusy(true);
    try {
      const r = await remindNonVoters(meetingId, motion.id);
      if (r.pending === 0) toast.success('Everyone has already voted — no reminders needed.');
      else if (r.sent === 0) toast.error(`Could not deliver reminders to ${r.pending} non-voter(s) — check email settings.`);
      else toast.success(`Reminder sent to ${r.sent} of ${r.pending} non-voter(s).`);
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send reminders.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg border p-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`motion-${motion.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-title flex items-start gap-2 flex-wrap" style={{ color: 'var(--navy)' }}><span>{motion.question}</span><ApplicationBadge motion={motion} canManage={canManage} /></div>
          <div className="flex items-center gap-2 text-xs mt-1 flex-wrap" style={{ color: 'var(--olive)' }}>
            <span className="px-2 py-0.5 rounded-full" style={{ background: 'var(--sage)' }}>{closed ? 'Closed' : 'Open'}</span>
            <span>· {motion.total_votes} vote{motion.total_votes === 1 ? '' : 's'}</span>
            <span className="inline-flex items-center gap-1"><EyeOff size={11} /> anonymous</span>
            {motion.closes_at && <span>· closes {new Date(motion.closes_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>}
            {motion.quorum ? <span className="px-2 py-0.5 rounded-full font-title" style={{ background: motion.quorum_met ? '#1c6b2c' : 'var(--sage)', color: motion.quorum_met ? '#fff' : 'var(--olive)' }}>{motion.quorum_met ? 'Quorum met' : `Quorum ${motion.total_votes}/${motion.quorum}`}</span> : null}
          </div>
          {(motion.quorum || motion.attendee_count) ? (
            <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,0.08)', maxWidth: 260 }} title={`${motion.total_votes} of ${motion.quorum || motion.attendee_count} voted`} data-testid={`motion-progress-${motion.id}`}>
              <div style={{ width: `${Math.min(100, (motion.total_votes / Math.max(1, motion.quorum || motion.attendee_count)) * 100)}%`, height: '100%', background: 'var(--gold)' }} />
            </div>
          ) : null}
        </div>
        {canManage && (
          <div className="flex gap-1 flex-shrink-0">
            {!closed && <button onClick={emailBallots} disabled={busy} title="Email ballots to attendees" className="p-1.5 rounded" style={{ background: 'var(--olive)', color: 'var(--cream)' }} data-testid={`motion-ballots-${motion.id}`}><Mail size={14} /></button>}
            {!closed && <button onClick={remind} disabled={busy} title="Remind attendees who haven't voted" className="p-1.5 rounded" style={{ background: 'var(--gold, #9a6b00)', color: '#fff' }} data-testid={`motion-remind-${motion.id}`}><BellRing size={14} /></button>}
            {!closed && <button onClick={close} disabled={busy} title="Close voting" className="p-1.5 rounded" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid={`motion-close-${motion.id}`}><Lock size={14} /></button>}
            <button onClick={del} disabled={busy} title="Delete motion" className="p-1.5 rounded" style={{ color: 'var(--maroon)' }} data-testid={`motion-delete-${motion.id}`}><Trash2 size={14} /></button>
          </div>
        )}
      </div>

      {!closed && (
        <div className="flex gap-2 mt-3" data-testid={`motion-vote-${motion.id}`}>
          {CHOICES.map((c) => {
            const Icon = c.icon; const active = motion.my_vote === c.v;
            return (
              <button key={c.v} onClick={() => vote(c.v)} disabled={busy} data-testid={`motion-vote-${motion.id}-${c.v}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-title"
                style={active ? { background: c.color, color: 'white' } : { background: 'white', color: 'var(--olive)', border: '1px solid rgba(0,0,0,0.12)' }}>
                {busy && active ? <Loader2 size={13} className="animate-spin" /> : <Icon size={14} />} {c.label}
              </button>
            );
          })}
        </div>
      )}

      {motion.my_vote && !closed && <p className="text-xs mt-2" style={{ color: 'var(--olive)' }}>You voted <strong>{motion.my_vote}</strong>. You can change it until voting closes.</p>}

      {motion.results_visible && motion.tally
        ? <TallyBar tally={motion.tally} />
        : <p className="text-xs mt-3 inline-flex items-center gap-1" style={{ color: 'var(--olive)' }} data-testid={`motion-hidden-${motion.id}`}><Lock size={12} /> Results are hidden until voting closes.</p>}
    </div>
  );
}

export default function MeetingVoting({ meeting, onUpdate }) {
  const { adminCreateMotion } = useAuth();
  const [q, setQ] = useState('');
  const [closesAt, setClosesAt] = useState('');
  const [quorum, setQuorum] = useState('');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const motions = meeting.motions || [];

  const add = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      onUpdate(await adminCreateMotion(meeting.id, {
        question: q.trim(),
        closes_at: closesAt ? new Date(closesAt).toISOString() : null,
        quorum: quorum ? Number(quorum) : null,
      }));
      setQ(''); setClosesAt(''); setQuorum(''); setAdding(false);
      toast.success('Motion opened for voting.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create the motion.'); }
    finally { setBusy(false); }
  };

  if (!meeting.can_manage && motions.length === 0) return null;

  return (
    <section className="mt-6" data-testid="detail-voting">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-title text-xl flex items-center gap-2" style={{ color: 'var(--maroon)' }}><Vote size={20} /> Motions &amp; voting</h2>
        {meeting.can_manage && !adding && (
          <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded font-title" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid="motion-add-btn"><Plus size={15} /> New motion</button>
        )}
      </div>

      {adding && (
        <div className="bg-white rounded-lg border p-3 mb-3 space-y-2" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="Motion, e.g. 'Approve the 2026 budget'" data-testid="motion-question-input" autoFocus />
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-[11px] mb-0.5" style={{ color: 'var(--olive)' }}>Closes (optional)</label>
              <Input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} className="w-52" data-testid="motion-closes-input" />
            </div>
            <div>
              <label className="block text-[11px] mb-0.5" style={{ color: 'var(--olive)' }}>Quorum (min votes)</label>
              <Input type="number" min={0} value={quorum} onChange={(e) => setQuorum(e.target.value)} className="w-32" placeholder="e.g. 5" data-testid="motion-quorum-input" />
            </div>
            <button onClick={add} disabled={busy} className="px-4 py-2 rounded text-sm font-title inline-flex items-center gap-1.5" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="motion-save-btn">{busy && <Loader2 size={13} className="animate-spin" />} Open vote</button>
            <button onClick={() => { setAdding(false); setQ(''); }} className="px-3 py-2 rounded text-sm" style={{ background: 'var(--sage)', color: 'var(--navy)' }}>Cancel</button>
          </div>
        </div>
      )}

      {motions.length === 0 ? <p className="text-sm" style={{ color: 'var(--olive)' }}>No motions yet.</p>
        : <div className="space-y-3">{motions.map((mo) => <Motion key={mo.id} meetingId={meeting.id} motion={mo} canManage={meeting.can_manage} onUpdate={onUpdate} />)}</div>}
    </section>
  );
}
